import React, { useState, useCallback, useEffect } from 'react';
import './DischargeDemo.css';
import {
  startEpicAuth,
  handleEpicCallback,
  isConnected,
  disconnect,
  fetchPatientContext,
  fhirToDischargePatient,
} from './epicFhir';
import WardOverview from './WardOverview';
import PatientComparison from './PatientComparison';
import CascadeVisualizer from './CascadeVisualizer';
import GenesisRouting from './GenesisRouting';
import MedicationList from './MedicationList';
import DischargePackage from './DischargePackage';

const PLACEMENT_LABELS = {
  'home': 'Home',
  'home_with_services': 'Home + Services',
  'snf': 'SNF',
  'rehab': 'Rehab (IRF)',
  'ltac': 'LTAC',
  'not_ready': 'Not Ready',
};

const API_URL = '/api/discharge';

// ============================================================
// Main Component — Ward Orchestrator
// ============================================================

function DischargeDemo() {
  // View state: 'ward', 'detail', or 'compare'
  const [view, setView] = useState('ward');
  const [wardResults, setWardResults] = useState(null);
  const [wardSummary, setWardSummary] = useState(null);
  const [selectedPatientResult, setSelectedPatientResult] = useState(null);
  const [activeTab, setActiveTab] = useState('cascade');

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Epic state
  const [epicConnected, setEpicConnected] = useState(false);
  const [epicError, setEpicError] = useState(null);

  // Check for Epic OAuth callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('code')) {
      handleEpicCallback()
        .then((token) => {
          if (token) {
            setEpicConnected(true);
            window.history.replaceState({}, '', window.location.pathname);
          }
        })
        .catch((err) => setEpicError(err.message));
    } else {
      setEpicConnected(isConnected());
    }
  }, []);

  const connectEpic = () => {
    startEpicAuth().catch((err) => setEpicError(err.message));
  };

  const disconnectEpic = () => {
    disconnect();
    setEpicConnected(false);
    setEpicError(null);
  };

  // Run ward-level batch assessment
  const runWardAssessment = useCallback(async () => {
    setProcessing(true);
    setError(null);
    setWardResults(null);
    setWardSummary(null);

    try {
      const response = await fetch(`${API_URL}/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      setWardResults(data.patients);
      setWardSummary(data.ward_summary);
    } catch (err) {
      console.error('Ward assessment error:', err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }, []);

  // Select a patient from the ward view
  const selectPatient = useCallback((patientId) => {
    if (!wardResults) return;
    const patient = wardResults.find(r => r.patientId === patientId);
    if (patient) {
      setSelectedPatientResult(patient);
      setActiveTab('cascade');
      setView('detail');
    }
  }, [wardResults]);

  const backToWard = () => {
    setView('ward');
    setSelectedPatientResult(null);
  };

  const chemBarClass = (val) => {
    if (val >= 0.7) return 'high';
    if (val <= 0.3) return 'low';
    return 'mid';
  };

  // ============================================================
  // Detail View
  // ============================================================
  const renderDetailView = () => {
    const r = selectedPatientResult;
    if (!r) return null;

    return (
      <div className="dd-detail-view">
        {/* Back Button */}
        <button className="dd-back-btn" onClick={backToWard}>
          {'\u2190'} Back to Ward Overview
        </button>

        {/* Patient Header */}
        <div className="dd-detail-header">
          <div className="dd-detail-header-left">
            <div className="dd-detail-name">{r.patientName}</div>
            <div className="dd-detail-meta">
              Age {r.age} | {r.insurance.charAt(0).toUpperCase() + r.insurance.slice(1)} |
              {' '}{r.unit} | Day {r.daysAdmitted} | {r.condition.charAt(0).toUpperCase() + r.condition.slice(1)}
            </div>
            <div className="dd-detail-diagnosis">{r.diagnosis.code}: {r.diagnosis.label}</div>
            <div className="dd-detail-summary">{r.summary}</div>
          </div>
          <div className="dd-detail-header-right">
            <div className="dd-detail-priority">Priority #{r.priority}</div>
            <div className={`dd-placement-badge ${r.placement === 'not_ready' ? 'not-ready' : r.placement === 'home' ? 'home' : ''}`}>
              {PLACEMENT_LABELS[r.placement]}
            </div>
            <div className="dd-readiness" style={{ justifyContent: 'flex-end' }}>
              <div className={`dd-readiness-dot ${r.readiness}`} />
              <span className="dd-readiness-text">{r.readiness.replace('_', ' ')}</span>
            </div>
          </div>
        </div>

        {/* Rationale */}
        <div className="dd-result-panel" style={{ marginBottom: '1rem' }}>
          <div className="dd-result-title">Discharge Decision Rationale</div>
          <div className="dd-rationale">{r.rationale}</div>
          {r.nca_factors && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-dim)' }}>
              Decision driver: <span style={{ color: 'var(--gold)' }}>{r.nca_factors.decision_driver}</span>
            </div>
          )}
        </div>

        {/* NCA Chemical State (always visible) */}
        <div className="dd-cascade" style={{ marginBottom: '1rem' }}>
          <div className="dd-cascade-title">NCA Cascade State (22D) - Live H-H Gating</div>
          <div className="dd-cascade-grid">
            {Object.entries(r.nca.chemicals).map(([name, val]) => (
              <div key={name} className="dd-chem-item">
                <span className="dd-chem-label">{name}</span>
                <div className="dd-chem-bar-bg">
                  <div className={`dd-chem-bar ${chemBarClass(val)}`} style={{ width: `${val * 100}%` }} />
                </div>
                <span className="dd-chem-value">{val.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="dd-gates">
            <div className="dd-gates-title">H-H Gated Brain Region Activations</div>
            <div className="dd-gates-grid">
              {Object.entries(r.nca.gates).map(([name, val]) => (
                <div key={name} className="dd-gate-item">
                  <span className="dd-gate-label">{name}</span>
                  <div className="dd-gate-bar-bg">
                    <div className="dd-gate-bar" style={{ width: `${val * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabbed Sections */}
        <div className="dd-tabs">
          <button className={`dd-tab ${activeTab === 'cascade' ? 'active' : ''}`} onClick={() => setActiveTab('cascade')}>
            Cascade
          </button>
          <button className={`dd-tab ${activeTab === 'genesis' ? 'active' : ''}`} onClick={() => setActiveTab('genesis')}>
            Genesis Cells
          </button>
          <button className={`dd-tab ${activeTab === 'medications' ? 'active' : ''}`} onClick={() => setActiveTab('medications')}>
            Medications
          </button>
          <button className={`dd-tab ${activeTab === 'package' ? 'active' : ''}`} onClick={() => setActiveTab('package')}>
            Discharge Package
          </button>
          <button className={`dd-tab ${activeTab === 'compliance' ? 'active' : ''}`} onClick={() => setActiveTab('compliance')}>
            Compliance
          </button>
        </div>

        <div className="dd-tab-content">
          {activeTab === 'cascade' && <CascadeVisualizer cascade={r.cascade} />}
          {activeTab === 'genesis' && <GenesisRouting genesis={r.genesis} />}
          {activeTab === 'medications' && <MedicationList medications={r.medications} />}
          {activeTab === 'package' && (
            <DischargePackage
              equipment={r.equipment}
              restrictions={r.restrictions}
              redFlags={r.redFlags}
              socialFactors={r.socialFactors}
            />
          )}
          {activeTab === 'compliance' && (
            <>
              <div className="dd-results">
                <div className="dd-result-panel">
                  <div className="dd-result-title">Authorization Steps</div>
                  <ul className="dd-list">
                    {r.authSteps.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                <div className="dd-result-panel">
                  <div className="dd-result-title">CMS Compliance Flags</div>
                  {r.complianceFlags.map((f, i) => (
                    <div key={i} className="dd-flag">{f}</div>
                  ))}
                </div>
              </div>
              <div className="dd-result-panel full-width" style={{ marginBottom: '1rem' }}>
                <div className="dd-result-title">Required Documentation ({r.docs.length} items)</div>
                <ul className="dd-list">
                  {r.docs.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
              <div className="dd-result-panel full-width" style={{ marginBottom: '1rem' }}>
                <div className="dd-result-title">Discharge Timeline</div>
                <div className="dd-timeline-step" style={{ fontWeight: 600, color: 'var(--gold-dim)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  <div>Action</div>
                  <div style={{ textAlign: 'center' }}>Deadline</div>
                  <div style={{ textAlign: 'right' }}>Responsible</div>
                </div>
                {r.timeline.map((t, i) => (
                  <div key={i} className="dd-timeline-step">
                    <div className="dd-timeline-action">{t.action}</div>
                    <div className="dd-timeline-deadline">{t.deadline}</div>
                    <div className="dd-timeline-owner">{t.owner}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* SIRA Audit Trail */}
        <div className="dd-sira">
          <div className="dd-sira-title">SIRA Audit Trail (SHA-256 State Hash)</div>
          <div className="dd-sira-hash">{r.stateHash}</div>
          {r.sira && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginBottom: '0.5rem', fontFamily: 'monospace' }}>
              SIRA ID: {r.sira.sira_id}
            </div>
          )}
          <div className="dd-sira-meta">
            <div className="dd-sira-item">
              <div className="dd-sira-label">Attributable</div>
              <div className="dd-sira-value pass">PASS</div>
            </div>
            <div className="dd-sira-item">
              <div className="dd-sira-label">Contemporaneous</div>
              <div className="dd-sira-value pass">PASS</div>
            </div>
            <div className="dd-sira-item">
              <div className="dd-sira-label">Original</div>
              <div className="dd-sira-value pass">PASS</div>
            </div>
            <div className="dd-sira-item">
              <div className="dd-sira-label">Accurate</div>
              <div className="dd-sira-value pass">PASS</div>
            </div>
          </div>
          <div style={{ marginTop: '0.8rem', fontSize: '0.75rem', color: 'var(--text-dim)', textAlign: 'center' }}>
            Processing time: {r.nca.processingTime}ms | Conflict level: {r.nca.conflictLevel.toFixed(3)} | Timestamp: {r.timestamp}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <section className="discharge-demo">
      <div className="section-inner">

        {/* Ward Header */}
        <div className="dd-ward-header">
          <div className="dd-ward-title-row">
            <div>
              <h1 className="dd-ward-title">Ward Discharge Coordination</h1>
              <p className="dd-ward-subtitle">
                NCA-powered discharge planning with real Hodgkin-Huxley conductance gating.
                15-patient ward assessment with cascade visualization, genesis cell routing,
                medication planning, and CMS compliance.
              </p>
            </div>
          </div>
        </div>

        {/* EHR Connection Bar */}
        <div className="dd-ehr-bar reveal">
          <div className="dd-ehr-left">
            <span className="dd-ehr-label">EHR Integration</span>
            <span className={`dd-ehr-status ${epicConnected ? 'connected' : ''}`}>
              {epicConnected ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          <div className="dd-ehr-right">
            {epicConnected ? (
              <button className="dd-ehr-button disconnect" onClick={disconnectEpic}>
                Disconnect Epic
              </button>
            ) : (
              <button className="dd-ehr-button connect" onClick={connectEpic}>
                Connect to Epic EHR
              </button>
            )}
          </div>
        </div>

        {epicError && (
          <div className="dd-epic-error">{epicError}</div>
        )}

        {/* Run Button */}
        {view === 'ward' && (
          <div className="dd-run-section reveal">
            <button
              className={`dd-run-button ${processing ? 'processing' : ''}`}
              onClick={runWardAssessment}
              disabled={processing}
            >
              {processing ? 'Processing 15 Patients Through NCA Cascade...' : 'Run Ward Assessment'}
            </button>
            {!wardResults && !processing && (
              <div className="dd-run-hint">
                Processes all 15 patients through real H-H conductance gating with cascade simulation
              </div>
            )}
          </div>
        )}

        {/* Processing Animation */}
        {processing && (
          <div className="dd-processing-overlay">
            <div className="dd-processing-text">
              Running 15 patients through 22D Hodgkin-Huxley conductance cascade...
            </div>
            <div className="dd-processing-sub">
              Signal transduction | H-H gating | Cascade derivation | Genesis routing | CMS compliance
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="dd-result-panel" style={{ borderColor: '#ef4444', marginBottom: '1rem' }}>
            <div className="dd-result-title" style={{ color: '#ef4444' }}>API Error</div>
            <div className="dd-rationale">{error}. The NCA discharge API may be restarting. Try again in a moment.</div>
          </div>
        )}

        {/* Ward View */}
        {view === 'ward' && wardResults && (
          <>
            <WardOverview
              wardResults={wardResults}
              wardSummary={wardSummary}
              onSelectPatient={selectPatient}
            />
            <div className="dd-compare-section">
              <button
                className="dd-compare-btn"
                onClick={() => setView('compare')}
              >
                Compare Patients — Why One Over Another?
              </button>
            </div>
          </>
        )}

        {/* Detail View */}
        {view === 'detail' && renderDetailView()}

        {/* Comparison View */}
        {view === 'compare' && wardResults && (
          <PatientComparison
            wardResults={wardResults}
            onBack={backToWard}
          />
        )}

        {/* Epic FHIR Badge */}
        <div className="dd-epic-badge">
          <div className="dd-epic-text">
            <strong>Epic FHIR R4 Integration Ready</strong> | SMART on FHIR OAuth2 + PKCE | Sandbox Client ID: 79817839...0df1 | Agent Factory Compatible
          </div>
        </div>

      </div>
    </section>
  );
}

export default DischargeDemo;
